import type { OutboxItem } from '@Domain/Ports/Outbox/OutboxPort';
import useOutboxPort from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxPort';
import * as React from 'react';

/**
 * Reflects the outbox port's queue through `useSyncExternalStore`. The stable-snapshot invariant
 * that `useSyncExternalStore` requires is carried by the port's `snapshot()` itself, which returns
 * the same array reference until the queue actually changes — this hook only wires the port, it
 * never needs to memoize the snapshot on top of it.
 */
function useOutboxItems(): readonly OutboxItem[] {
	const outboxPort = useOutboxPort();

	const subscribe = React.useCallback(
		(onStoreChange: () => void): (() => void) => outboxPort.subscribe(() => onStoreChange()),
		[outboxPort],
	);

	const getSnapshot = React.useCallback((): readonly OutboxItem[] => outboxPort.snapshot(), [outboxPort]);

	return React.useSyncExternalStore(subscribe, getSnapshot);
}

export default useOutboxItems;
